// frontend/src/settings/index.js
// 設定画面 (X 風サイドナビ + in-page 右ペイン render)
// - 左サイドバー: カテゴリ + 設定リスト (検索可)
// - 右メイン: 選択中の設定コンポーネントを直接 render
// - 各設定コンポーネントは ?embed=1 を見て独自 Header を抑制 (実装は各設定側に任せ、当面は重なってもOK)

import React, { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, ArrowLeft, ExternalLink, ChevronRight,
  Users, Briefcase, Stethoscope, ClipboardList, CalendarDays, Target,
  Link2, GitMerge, Calendar, Clock, KeyRound, Bell, Settings as SettingsIcon,
  UserCog, ShieldCheck, Key,
} from "lucide-react";
import Header from "../components/ui/Header";

// モバイル判定 (resize 対応)
function useIsMobile(breakpoint = 768) {
  const query = `(max-width: ${breakpoint}px)`;
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [query]);
  return isMobile;
}

// =============================================================================
// 各設定コンポーネント (lazy import で初期描画を軽量化)
// =============================================================================
const StaffSettings              = lazy(() => import("./StaffSettings"));
const JobTypeSettings            = lazy(() => import("./JobTypeSettings"));
const ShiftPatternSettings       = lazy(() => import("./ShiftPatternSettings"));
const SkillGroupSettings         = lazy(() => import("./SkillGroupSettings"));
const ShiftCompatibilitySettings = lazy(() => import("./ShiftCompatibilitySettings"));
const HolidaySettings            = lazy(() => import("./HolidaySettings"));
const AnnualHolidaySettings      = lazy(() => import("./AnnualHolidaySettings"));
const LinkSettings               = lazy(() => import("./LinkSettings"));
const ShiftDeadlineSettings      = lazy(() => import("./ShiftDeadlineSettings"));
const PasswordPrefixSettings     = lazy(() => import("./PasswordPrefixSettings"));
const InstructionNotificationSettings = lazy(() => import("./InstructionNotificationSettings"));
const DailyRoleSettings = lazy(() => import("./DailyRoleSettings"));
const AuditLogSettings           = lazy(() => import("./AuditLogSettings"));
const PasskeySettings            = lazy(() => import("./PasskeySettings"));

const SETTINGS = [
  {
    category: "組織管理",
    items: [
      { id: "staff",      title: "スタッフ管理",       description: "スタッフの登録・編集・削除", Icon: Users,       Component: StaffSettings },
      { id: "job-types",  title: "職種設定",          description: "看護師・看護助手・クラーク等", Icon: Briefcase,   Component: JobTypeSettings },
      { id: "procedures", title: "診療科・術式登録",   description: "診療科 / 術式 / レベル設定", Icon: Stethoscope, path: "/procedure-master" }, // 別ページに飛ばす
    ],
  },
  {
    category: "シフト",
    items: [
      { id: "shift-patterns",       title: "勤務パターン",   description: "勤務種別・必須役割・連鎖", Icon: ClipboardList, Component: ShiftPatternSettings },
      { id: "daily-roles",          title: "日次役割マスタ", description: "リーダー等の日次役割 (日勤者から選ぶ)", Icon: UserCog,    Component: DailyRoleSettings },
      { id: "skill-groups",         title: "スキルグループ", description: "スキル分類とメンバー管理",  Icon: Target,        Component: SkillGroupSettings },
      { id: "shift-compatibility",  title: "相性設定",      description: "同日割当不可ペアの管理",   Icon: GitMerge,      Component: ShiftCompatibilitySettings },
      { id: "holidays",             title: "祝祭日",         description: "祝日マスタの管理",          Icon: CalendarDays,  Component: HolidaySettings },
      { id: "annual-holidays",      title: "年間休日",       description: "月間/年間の休日上限",      Icon: Calendar,      Component: AnnualHolidaySettings },
    ],
  },
  {
    category: "運用",
    items: [
      { id: "shift-deadline",            title: "希望提出締切",    description: "希望の締切日",          Icon: Clock,    Component: ShiftDeadlineSettings },
      { id: "password-prefix",           title: "初期パスワード",  description: "新規スタッフ初期パスワード", Icon: KeyRound, Component: PasswordPrefixSettings },
      { id: "instruction-notification",  title: "指導通知",        description: "経験録更新通知の設定",  Icon: Bell,     Component: InstructionNotificationSettings },
    ],
  },
  {
    category: "リンク",
    items: [
      { id: "links", title: "リンク設定", description: "出退勤・手順書 URL", Icon: Link2, Component: LinkSettings },
    ],
  },
  {
    category: "セキュリティ",
    items: [
      { id: "passkey",    title: "パスキー",   description: "Touch ID / Face ID / セキュリティキーでログイン", Icon: Key,          Component: PasskeySettings },
      { id: "audit-logs", title: "監査ログ",   description: "ログイン・変更履歴の追跡 (管理者専用)",             Icon: ShieldCheck, Component: AuditLogSettings },
    ],
  },
];

const ALL_ITEMS = SETTINGS.flatMap((cat) => cat.items.map((i) => ({ ...i, category: cat.category })));

// =============================================================================
// メイン
// =============================================================================
function SettingsHome() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(ALL_ITEMS[0].id);

  const filtered = useMemo(() => {
    if (!search.trim()) return SETTINGS;
    const kw = search.toLowerCase();
    return SETTINGS.map((cat) => ({
      ...cat,
      items: cat.items.filter((it) =>
        it.title.toLowerCase().includes(kw) || it.description.toLowerCase().includes(kw)
      ),
    })).filter((cat) => cat.items.length > 0);
  }, [search]);

  const activeItem = ALL_ITEMS.find((i) => i.id === activeId) || ALL_ITEMS[0];

  // デスクトップ: 右ペインに embed。
  // モバイル: タップで /settings/<id> に遷移してフル画面表示 (個別ルートが存在)。
  const handleSelect = (item) => {
    if (item.path) {
      navigate(item.path);
      return;
    }
    if (isMobile) {
      navigate(`/settings/${item.id}`);
      return;
    }
    setActiveId(item.id);
  };

  const ActiveComponent = activeItem.Component;

  // =============================================================================
  // モバイル: X アプリ風フル画面リスト
  // =============================================================================
  if (isMobile) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={mobileStyles.wrap}>
          <div style={mobileStyles.topBar}>
            <button onClick={() => navigate('/home')} style={mobileStyles.backBtn} aria-label="戻る">
              <ArrowLeft size={20} />
            </button>
            <h1 style={mobileStyles.title}>設定</h1>
            <div style={{ width: 32 }} />
          </div>

          <div style={mobileStyles.searchBox}>
            <Search size={16} style={mobileStyles.searchIcon} />
            <input
              type="text"
              placeholder="設定を検索"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={mobileStyles.searchInput}
            />
          </div>

          <div style={mobileStyles.list}>
            {filtered.length === 0 && (
              <div style={mobileStyles.empty}>該当する設定はありません</div>
            )}
            {filtered.map((cat) => (
              <section key={cat.category} style={mobileStyles.section}>
                <div style={mobileStyles.sectionLabel}>{cat.category}</div>
                <div style={mobileStyles.card}>
                  {cat.items.map((it, idx) => (
                    <button
                      key={it.id}
                      onClick={() => handleSelect(it)}
                      style={{
                        ...mobileStyles.row,
                        borderBottom: idx === cat.items.length - 1 ? "none" : "1px solid #e5e7eb",
                      }}
                    >
                      <div style={mobileStyles.rowIcon}>
                        <it.Icon size={18} color="#1d4ed8" />
                      </div>
                      <div style={mobileStyles.rowText}>
                        <div style={mobileStyles.rowTitle}>{it.title}</div>
                        <div style={mobileStyles.rowDesc}>{it.description}</div>
                      </div>
                      {it.path
                        ? <ExternalLink size={14} style={{ color: "#94a3b8", flexShrink: 0 }} />
                        : <ChevronRight size={18} style={{ color: "#cbd5e1", flexShrink: 0 }} />
                      }
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // =============================================================================
  // デスクトップ: サイドバー + 右ペイン embed
  // =============================================================================
  return (
    <div style={styles.container}>
      {/* 🔥 embed する各設定コンポーネントが Header を独自に持つ場合があるので、
          その重複表示を CSS で隠す: 中の Header (header / .header-* / nav 等) を強制非表示。
          SettingsHome 自体の Header は外側で表示。 */}
      <style>{`
        .settings-embed-area > div > header,
        .settings-embed-area > header,
        .settings-embed-area .procedure-master-container > header { display: none !important; }
        .settings-embed-area body { padding-top: 0 !important; margin-left: 0 !important; }
        .settings-embed-area > div { padding-top: 0 !important; }
      `}</style>
      <Header />
      <div style={styles.shell}>
        {/* ===== 左サイドバー ===== */}
        <aside style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <button onClick={() => navigate('/home')} style={styles.backBtn}>
              <ArrowLeft size={14} /> ホーム
            </button>
            <h1 style={styles.sidebarTitle}>
              <SettingsIcon size={20} /> 設定
            </h1>
          </div>

          <div style={styles.searchBox}>
            <Search size={14} style={styles.searchIcon} />
            <input type="text" placeholder="設定を検索" value={search}
              onChange={(e) => setSearch(e.target.value)} style={styles.searchInput} />
          </div>

          <div style={styles.navList}>
            {filtered.length === 0 && <div style={styles.empty}>該当なし</div>}
            {filtered.map((cat) => (
              <div key={cat.category} style={styles.navCategory}>
                <div style={styles.navCategoryLabel}>{cat.category}</div>
                {cat.items.map((it) => {
                  const isActive = !it.path && activeItem?.id === it.id;
                  return (
                    <button key={it.id} onClick={() => handleSelect(it)} style={{
                      ...styles.navItem,
                      background: isActive ? '#eff6ff' : 'transparent',
                      color: isActive ? '#1d4ed8' : '#0f172a',
                      fontWeight: isActive ? 600 : 400,
                    }}>
                      <it.Icon size={16} style={{ flexShrink: 0, opacity: isActive ? 1 : 0.6 }} />
                      <span style={styles.navItemLabel}>{it.title}</span>
                      {it.path && <ExternalLink size={11} style={{ opacity: 0.5 }} />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        {/* ===== 右メイン (選択した設定を embed) ===== */}
        <main style={styles.main}>
          <div style={styles.mainHeader}>
            <activeItem.Icon size={20} style={{ color: '#1d4ed8' }} />
            <div>
              <div style={styles.mainCategory}>{activeItem.category}</div>
              <h2 style={styles.mainTitle}>{activeItem.title}</h2>
            </div>
            <p style={styles.mainDescription}>{activeItem.description}</p>
          </div>

          <div style={styles.embedFrame} className="settings-embed-area">
            <Suspense fallback={<div style={styles.loading}>読み込み中...</div>}>
              {ActiveComponent && <ActiveComponent embedded={true} />}
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}

// =============================================================================
// styles
// =============================================================================
const styles = {
  container: { minHeight: "100vh", background: "#f8fafc" },
  shell: {
    maxWidth: "1400px", margin: "0 auto",
    padding: "24px 24px calc(90px + env(safe-area-inset-bottom))",
    display: "grid", gridTemplateColumns: "260px 1fr",
    gap: "20px", alignItems: "start",
  },
  sidebar: {
    background: "white", borderRadius: "14px", border: "1px solid #e5e7eb",
    padding: "16px", position: "sticky", top: "76px",
    // 下部 FooterNav (fixed, 70px) に被らないよう余裕を見て 170px 引く
    maxHeight: "calc(100vh - 170px)", overflowY: "auto",
  },
  sidebarHeader: { marginBottom: "14px" },
  backBtn: {
    background: "transparent", border: "none", color: "#3b82f6",
    fontSize: "12px", cursor: "pointer", display: "inline-flex",
    alignItems: "center", gap: "4px", padding: 0, marginBottom: "6px",
  },
  sidebarTitle: {
    fontSize: "18px", fontWeight: 700, color: "#0f172a", margin: 0,
    display: "flex", alignItems: "center", gap: "8px",
  },
  searchBox: { position: "relative", marginBottom: "14px" },
  searchIcon: {
    position: "absolute", left: "10px", top: "50%",
    transform: "translateY(-50%)", color: "#94a3b8",
  },
  searchInput: {
    width: "100%", padding: "8px 10px 8px 32px", borderRadius: "10px",
    border: "1px solid #e5e7eb", fontSize: "13px",
    background: "#f8fafc", boxSizing: "border-box", outline: "none",
  },
  navList: { display: "flex", flexDirection: "column", gap: "16px" },
  empty: { color: "#94a3b8", fontSize: "12px", textAlign: "center", padding: "16px" },
  navCategory: { display: "flex", flexDirection: "column", gap: "2px" },
  navCategoryLabel: {
    fontSize: "10px", fontWeight: 700, color: "#94a3b8",
    textTransform: "uppercase", letterSpacing: "0.5px",
    padding: "0 8px", marginBottom: "4px",
  },
  navItem: {
    display: "flex", alignItems: "center", gap: "10px",
    padding: "8px 10px", borderRadius: "8px", border: "none",
    background: "transparent", cursor: "pointer", fontSize: "13px",
    textAlign: "left", width: "100%", transition: "background 0.1s",
  },
  navItemLabel: {
    flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  main: { minWidth: 0 },
  mainHeader: {
    background: "white", borderRadius: "14px", padding: "16px 20px",
    border: "1px solid #e5e7eb", marginBottom: "16px",
    display: "grid", gridTemplateColumns: "auto 1fr", gap: "12px 14px",
    alignItems: "center",
  },
  mainCategory: {
    fontSize: "10px", fontWeight: 700, color: "#3b82f6",
    textTransform: "uppercase", letterSpacing: "0.5px",
  },
  mainTitle: {
    fontSize: "20px", fontWeight: 700, color: "#0f172a", margin: 0,
  },
  mainDescription: {
    fontSize: "12px", color: "#64748b", margin: 0,
    gridColumn: "1 / 3",
  },
  embedFrame: {
    background: "white", borderRadius: "14px",
    border: "1px solid #e5e7eb", overflow: "hidden",
    minHeight: "400px",
  },
  loading: {
    padding: "60px", textAlign: "center", color: "#94a3b8", fontSize: 14,
  },
};

// =============================================================================
// モバイル用 styles (X / Instagram アプリ風のフル画面リスト)
// =============================================================================
const mobileStyles = {
  wrap: {
    maxWidth: "100%",
    // 下部余白は FooterNav (fixed, 70px) + 安全マージンで 110px。
    // iOS Safari のホームインジケータ分も考慮。
    padding: "12px 14px calc(110px + env(safe-area-inset-bottom))",
    boxSizing: "border-box",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
    paddingTop: 4,
  },
  backBtn: {
    background: "transparent",
    border: "none",
    color: "#1e293b",
    cursor: "pointer",
    padding: 6,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#0f172a",
    margin: 0,
    textAlign: "center",
    flex: 1,
  },
  searchBox: {
    position: "relative",
    marginBottom: 16,
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#94a3b8",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "11px 12px 11px 38px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    fontSize: 15,
    background: "#f1f5f9",
    boxSizing: "border-box",
    outline: "none",
    appearance: "none",
    WebkitAppearance: "none",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  empty: {
    color: "#94a3b8",
    fontSize: 13,
    textAlign: "center",
    padding: "32px 0",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    padding: "0 4px",
  },
  card: {
    background: "white",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "14px 14px",
    background: "white",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    WebkitTapHighlightColor: "rgba(59, 130, 246, 0.1)",
  },
  rowIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: 2,
  },
  rowDesc: {
    fontSize: 12,
    color: "#64748b",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

export default SettingsHome;
